import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { type ApiError, api, qs, session, unwrap } from '../api/client';
import { useAccounts, useAutomations } from '../api/hooks';
import type {
  Contact,
  ContactDetail,
  ContactListItem,
  ContactSegment,
  Items,
  Page,
} from '../api/types';
import { useSession } from '../auth/session';
import {
  Badge,
  Card,
  DataTable,
  Empty,
  ErrorAlert,
  Field,
  fmtDate,
  Modal,
  PageHeader,
  useToast,
} from '../components/ui';

type ListFilter = { q: string; tag: string; source: string };
const EMPTY_FILTER: ListFilter = { q: '', tag: '', source: '' };

/** Saved segments as chips: click applies the filter, × removes, "Save" stores the current one. */
function SegmentChips(props: { filter: ListFilter; onApply: (f: ListFilter) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const segments = useQuery({
    queryKey: ['contact-segments'],
    queryFn: () => unwrap(api.get<Items<ContactSegment>>('/portal/contacts/segments')),
  });
  const save = useMutation({
    mutationFn: (name: string) =>
      unwrap(
        api.post('/portal/contacts/segments', {
          name,
          filter: Object.fromEntries(Object.entries(props.filter).filter(([, v]) => v !== '')),
        }),
      ),
    onSuccess: () => {
      toast('Segment saved');
      return qc.invalidateQueries({ queryKey: ['contact-segments'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.del(`/portal/contacts/segments/${id}`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-segments'] }),
  });
  const dirty = Object.values(props.filter).some((v) => v !== '');
  const active = (s: ContactSegment) =>
    (s.filter.q ?? '') === props.filter.q &&
    (s.filter.tag ?? '') === props.filter.tag &&
    (s.filter.source ?? '') === props.filter.source;
  return (
    <div className="pill-row" style={{ marginBottom: 8 }} data-testid="segments">
      <span className="muted small">Segments:</span>
      {(segments.data?.items ?? []).map((s) => (
        <span key={s.id} className={`badge ${active(s) ? 'badge-primary' : ''}`}>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={() => props.onApply({ ...EMPTY_FILTER, ...s.filter })}
          >
            {s.name}
          </button>
          {can('contact:manage') ? (
            <button
              type="button"
              className="btn btn-link btn-sm"
              aria-label={`delete segment ${s.name}`}
              onClick={() => remove.mutate(s.id)}
            >
              ×
            </button>
          ) : null}
        </span>
      ))}
      {segments.data?.items.length === 0 ? <span className="muted small">none saved</span> : null}
      {can('contact:manage') && dirty ? (
        <button
          type="button"
          className="btn btn-sm"
          disabled={save.isPending}
          onClick={() => {
            const name = window.prompt('Name this segment');
            if (name) save.mutate(name);
          }}
        >
          Save current filter
        </button>
      ) : null}
      <ErrorAlert error={(save.error ?? remove.error) as ApiError | null} />
    </div>
  );
}

export function ContactsPage() {
  const [params] = useSearchParams();
  const [filter, setFilter] = useState<ListFilter>(EMPTY_FILTER);
  const { can } = useSession();
  const qc = useQueryClient();
  // CON-14: the list pages through the API's cursor
  const q = useInfiniteQuery({
    queryKey: ['contacts', filter, params.get('flow')],
    queryFn: ({ pageParam }) =>
      unwrap(
        api.get<Page<ContactListItem>>(
          `/portal/contacts${qs({ ...filter, flowId: params.get('flow') ?? undefined, cursor: pageParam ?? undefined })}`,
        ),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const contactItems = q.data?.pages.flatMap((pg) => pg.items) ?? [];
  const tag = useMutation({
    mutationFn: (v: { id: string; tag: string }) =>
      unwrap(api.post(`/portal/contacts/${v.id}/tags`, { tag: v.tag })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  });
  return (
    <>
      <PageHeader
        title="Contacts / Audience"
        subtitle={
          params.get('flow')
            ? 'Contacts who entered the selected flow'
            : 'Everyone your automations have talked to'
        }
      />
      <SegmentChips filter={filter} onApply={setFilter} />
      <div className="row" style={{ marginBottom: 12 }}>
        <input
          className="input"
          style={{ maxWidth: 240 }}
          placeholder="Search username or name"
          value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}
        />
        <input
          className="input"
          style={{ maxWidth: 160 }}
          placeholder="Tag"
          value={filter.tag}
          onChange={(e) => setFilter({ ...filter, tag: e.target.value })}
        />
        <select
          className="input"
          style={{ maxWidth: 160 }}
          value={filter.source}
          onChange={(e) => setFilter({ ...filter, source: e.target.value })}
        >
          <option value="">Any source</option>
          {[
            'comment',
            'story_reply',
            'dm',
            'story_mention',
            'post_share',
            'mention',
            'api',
            'manual',
          ].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </div>
      <ErrorAlert error={q.error} />
      <DataTable>
        <table className="table">
          <thead>
            <tr>
              <th>Contact ID</th>
              <th>Username</th>
              <th>Display name</th>
              <th>First seen</th>
              <th>Last seen</th>
              <th>Source</th>
              <th>Tags</th>
              <th>Automations</th>
              <th>Clicks</th>
              <th>Converted</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {contactItems.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={`/app/contacts/${c.id}`} className="mono">
                    {c.id.slice(-8)}
                  </Link>
                </td>
                <td>@{c.username ?? c.audienceUserId}</td>
                <td>{c.name ?? '—'}</td>
                <td>{fmtDate(c.firstSeenAt)}</td>
                <td>{fmtDate(c.lastSeenAt)}</td>
                <td>
                  {/* CON-15: the media kind that brought the contact in */}
                  {c.firstMediaKind
                    ? `${c.source.replace('_', ' ')} · ${c.firstMediaKind}`
                    : c.source.replace('_', ' ')}
                </td>
                <td className="pill-row">
                  {c.tags.map((t) => (
                    <span key={t} className="badge badge-primary">
                      {t}
                    </span>
                  ))}
                  {can('contact:manage') ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-link"
                      onClick={() => {
                        const t = window.prompt('Tag to add');
                        if (t) tag.mutate({ id: c.id, tag: t });
                      }}
                    >
                      + tag
                    </button>
                  ) : null}
                </td>
                <td className="pill-row">
                  <AutomationsCell automations={c.automations} />
                </td>
                <td>{c.clicks}</td>
                <td>{c.converted ? 'yes' : 'no'}</td>
                <td>
                  <Badge value={c.status} />
                </td>
              </tr>
            ))}
            {q.data && contactItems.length === 0 ? (
              <tr>
                <td colSpan={11}>
                  <Empty>No contacts yet. They appear as soon as an automation runs.</Empty>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </DataTable>
      {q.hasNextPage ? (
        <p>
          <button
            type="button"
            className="btn"
            disabled={q.isFetchingNextPage}
            onClick={() => q.fetchNextPage()}
          >
            Load more
          </button>
        </p>
      ) : null}
    </>
  );
}

/** UI-68: the account's handle, or its id tail until the accounts arrive. */
export const accountHandle = (
  accounts: ReadonlyArray<{ id: string; username: string }>,
  accountId: string,
): string => {
  const u = accounts.find((a) => a.id === accountId)?.username;
  return u ? `@${u}` : accountId.slice(-8);
};

/** UI-67: one automation shows its name; several show a count that expands to the names. */
function AutomationsCell({ automations }: { automations: { id: string; name: string }[] }) {
  const [open, setOpen] = useState(false);
  if (automations.length === 0) return <span className="muted">—</span>;
  if (automations.length === 1 || open)
    return (
      <>
        {automations.map((a) => (
          <Link key={a.id} to="/app/automations" className="badge">
            {a.name}
          </Link>
        ))}
      </>
    );
  return (
    <button type="button" className="btn btn-sm btn-link" onClick={() => setOpen(true)}>
      {automations.length} automations
    </button>
  );
}

export function ContactProfilePage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const accounts = useAccounts();
  const qc = useQueryClient();
  const unlockToken = session.unlock.get();
  const automations = useAutomations();
  const automationName = (aid: string | null): string =>
    aid === null ? '—' : (automations.data?.items.find((a) => a.id === aid)?.name ?? aid);
  const basic = useQuery({
    queryKey: ['contact', id],
    queryFn: () =>
      unwrap(api.get<{ contact: Contact; locked?: boolean }>(`/portal/contacts/${id}`)),
  });
  const profile = useQuery({
    queryKey: ['contact-profile', id, unlockToken],
    queryFn: () =>
      unwrap(
        api.get<ContactDetail>(
          `/portal/contacts/${id}/profile`,
          unlockToken ? { 'x-unlock-token': unlockToken } : {},
        ),
      ),
    retry: false,
  });
  const [unlocking, setUnlocking] = useState(false);
  const [code, setCode] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const requestCode = useMutation({
    mutationFn: () => unwrap(api.post<{ ok: true; devCode?: string }>('/portal/security/otp')),
    onSuccess: (r) => {
      setDevCode(r.devCode ?? null);
      toast('Code sent to your email');
    },
  });
  const unlock = useMutation({
    mutationFn: () =>
      unwrap(
        api.post<{ unlockToken: string }>(
          '/portal/security/unlock',
          apiKey ? { apiKey } : { code },
        ),
      ),
    onSuccess: async (r) => {
      session.unlock.set(r.unlockToken);
      setUnlocking(false);
      await qc.invalidateQueries({ queryKey: ['contact-profile', id] });
      toast('Sensitive data unlocked for 30 minutes');
    },
  });
  const removeTag = useMutation({
    mutationFn: (t: string) =>
      unwrap(api.del(`/portal/contacts/${id}/tags/${encodeURIComponent(t)}`)),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['contact', id] }),
        qc.invalidateQueries({ queryKey: ['contact-profile'] }),
      ]),
  });
  const c = basic.data?.contact;
  const locked =
    profile.isError && (profile.error as unknown as ApiError).code === 'unlock_required';
  const d = profile.data;
  return (
    <>
      <PageHeader
        title={c ? `@${c.username ?? c.audienceUserId}` : 'Contact'}
        subtitle="Contact profile"
        actions={
          <Link className="btn" to="/app/contacts">
            Back
          </Link>
        }
      />
      <ErrorAlert error={basic.error} />
      {locked ? (
        <div className="alert alert-info">
          This profile holds confidential data.{' '}
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => {
              setUnlocking(true);
              requestCode.mutate();
            }}
          >
            Unlock with email code or API key
          </button>
        </div>
      ) : null}
      {profile.isError && !locked ? <ErrorAlert error={profile.error} /> : null}
      {c ? (
        <div className="grid grid-3">
          <Card title="Basic information">
            <dl className="kv">
              <dt>Username</dt>
              <dd>@{c.username ?? '—'}</dd>
              <dt>Platform</dt>
              <dd>instagram</dd>
              <dt>Account</dt>
              {/* UI-68 */}
              <dd title={c.accountId}>{accountHandle(accounts.data?.items ?? [], c.accountId)}</dd>
              <dt>First interaction</dt>
              <dd>{fmtDate(c.firstSeenAt)}</dd>
              <dt>Last interaction</dt>
              <dd>{fmtDate(c.lastSeenAt)}</dd>
              <dt>Follower</dt>
              <dd>{c.isFollower === null ? 'unknown' : c.isFollower ? 'yes' : 'no'}</dd>
            </dl>
          </Card>
          <Card title="Engagement">
            {d ? (
              <dl className="kv">
                <dt>Comments</dt>
                <dd>{d.engagement.comments}</dd>
                <dt>DMs</dt>
                <dd>{d.engagement.dmsReceived}</dd>
                <dt>Messages received</dt>
                <dd>{d.engagement.messagesReceived}</dd>
                <dt>Messages sent</dt>
                <dd>{d.engagement.messagesSent}</dd>
                <dt>Link clicks</dt>
                <dd>{d.engagement.linkClicks}</dd>
              </dl>
            ) : (
              <p className="muted">Unlock to view.</p>
            )}
          </Card>
          <Card title="Attribution">
            {d ? (
              <dl className="kv">
                <dt>First automation</dt>
                <dd>{automationName(d.attribution.firstAutomationId)}</dd>
                <dt>Last automation</dt>
                <dd>{automationName(d.attribution.lastAutomationId)}</dd>
                <dt>Campaign</dt>
                <dd>{d.attribution.campaign ?? '—'}</dd>
                <dt>First click</dt>
                <dd>{fmtDate(d.attribution.firstClickAt)}</dd>
                <dt>Last click</dt>
                <dd>{fmtDate(d.attribution.lastClickAt)}</dd>
                <dt>Conversion</dt>
                <dd>{d.attribution.converted ? 'yes' : 'no'}</dd>
              </dl>
            ) : (
              <p className="muted">Unlock to view.</p>
            )}
          </Card>
          <Card title="Tags">
            <div className="pill-row">
              {c.tags.map((t) => (
                <span key={t} className="badge badge-primary">
                  {t}{' '}
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={() => removeTag.mutate(t)}
                    aria-label={`remove ${t}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              {c.tags.length === 0 ? <span className="muted">No tags</span> : null}
            </div>
          </Card>
          <Card title="Collected data">
            {d ? (
              Object.keys(d.contact.customFields).length ? (
                <dl className="kv">
                  {Object.entries(d.contact.customFields).map(([k, v]) => (
                    <>
                      <dt key={`${k}k`}>{k}</dt>
                      <dd key={`${k}v`}>{v}</dd>
                    </>
                  ))}
                </dl>
              ) : (
                <p className="muted">Nothing collected yet.</p>
              )
            ) : (
              <p className="muted">Unlock to view.</p>
            )}
          </Card>
          <Card title="Conversations">
            {d ? (
              <table className="table" data-testid="contact-conversations">
                <thead>
                  <tr>
                    <th>DM trigger</th>
                    <th>Post / Story / Reel</th>
                    <th>DM sent</th>
                    <th>Click initiated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {d.conversations.map((r) => (
                    <tr key={r.runId}>
                      <td>
                        {r.automationName}{' '}
                        <span className="muted small">({r.trigger.replace('_', ' ')})</span>
                      </td>
                      <td>
                        {r.media
                          ? `${r.media.kind}: ${r.media.caption ?? r.media.externalId}`
                          : '—'}
                      </td>
                      <td>{fmtDate(r.dmSentAt)}</td>
                      <td>{r.clicked ? 'Yes' : 'No'}</td>
                      <td>
                        {r.conversationId ? (
                          <Link to={`/app/inbox?tab=dms&conversation=${r.conversationId}`}>
                            View conversation
                          </Link>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {d.conversations.length === 0 ? (
                    <tr>
                      <td className="muted" colSpan={5}>
                        No automation has messaged this contact yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            ) : (
              <p className="muted">Unlock to view.</p>
            )}
          </Card>
          <Card title="History">
            {d ? (
              <DataTable>
                <table className="table">
                  <tbody>
                    {d.history.map((h) => (
                      <tr key={h.ref + h.at}>
                        <td>{fmtDate(h.at)}</td>
                        <td>
                          <Badge value={h.type} />
                        </td>
                        <td>
                          {h.conversationId ? (
                            <Link to={`/app/inbox?tab=dms&conversation=${h.conversationId}`}>
                              {h.summary}
                            </Link>
                          ) : (
                            h.summary
                          )}
                        </td>
                      </tr>
                    ))}
                    {d.history.length === 0 ? (
                      <tr>
                        <td className="muted">No history yet.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </DataTable>
            ) : (
              <p className="muted">Unlock to view.</p>
            )}
          </Card>
        </div>
      ) : null}
      {unlocking ? (
        <Modal
          title="Unlock sensitive data"
          onClose={() => setUnlocking(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setUnlocking(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={unlock.isPending}
                onClick={() => unlock.mutate()}
              >
                Unlock
              </button>
            </>
          }
        >
          {/* UI-47: a refused code request (rate limit, mail failure) is said */}
          <ErrorAlert error={(requestCode.error ?? unlock.error) as ApiError | null} />
          {devCode ? (
            <div className="alert alert-info">
              Development mode: your code is{' '}
              <strong data-testid="unlock-dev-code">{devCode}</strong>
            </div>
          ) : null}
          <Field label="Code from your email">
            <input
              className="input mono"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6}
            />
          </Field>
          <p className="muted small">or</p>
          <Field label="Workspace API key">
            <input
              className="input mono"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="adm_live_…"
            />
          </Field>
        </Modal>
      ) : null}
    </>
  );
}
